import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, ArrowRight, Check, CheckCircle2, Cloud, CreditCard, FolderOpen, HardDrive, Images, Mail, RefreshCcw, ShieldCheck, Sparkles } from 'lucide-react'
import { GmailPreflight, type GmailPreflightSummary } from './components/GmailPreflight'
import { CloudVaultCard } from './components/CloudVaultCard'
import { authorizeDrive, isValidClientId, listDriveItems } from './lib/drive'
import { pickDestination, supportsLocalDestination } from './lib/filesystem'
import { fileMatchesRules, formatBytes, rulesForRecipe } from './lib/rules'
import { licenseDurationLabel } from './lib/license'
import type { BackupDatePreset, BackupDestinationMode, BackupIntent, BackupSourceId, CopyRules, DriveItem, OrganizationMode, PendingBackupPlan } from './types'
import type { CloudQuote } from './lib/cloud'

type AnalysisStatus = 'idle' | 'connecting' | 'analyzing' | 'ready' | 'error'
type SelectionScope = 'all' | 'selection'
type SelectionTimeMode = 'recent' | 'range'
type PeriodUnit = 'months' | 'years'

type AnalysisState = {
  status: AnalysisStatus
  items: DriveItem[]
  message: string
}

type EffectiveDate = {
  datePreset: BackupDatePreset
  after?: string
  before?: string
  label: string
}

const sources: Array<{ id: BackupSourceId; title: string; description: string; detail: string; icon: typeof Mail }> = [
  { id: 'gmail', title: 'Gmail', description: 'Correos completos y adjuntos.', detail: 'Conexión directa de solo lectura.', icon: Mail },
  { id: 'photos', title: 'Google Fotos', description: 'Fotos y vídeos originales.', detail: 'Importación local de biblioteca oficial.', icon: Images },
  { id: 'drive', title: 'Google Drive', description: 'Documentos, carpetas y archivos.', detail: 'Conexión directa de solo lectura.', icon: FolderOpen },
]

const intents: Array<{ id: Extract<BackupIntent, 'backup' | 'clean'>; title: string; description: string }> = [
  { id: 'backup', title: 'Copiar', description: 'Guarda lo que elijas en una ubicación que controlarás tú.' },
  { id: 'clean', title: 'Limpiar', description: 'Prepara una revisión segura de lo que podrías liberar después de protegerlo.' },
]

const destinationOptions: Array<{ id: BackupDestinationMode; title: string; description: string; note: string; icon: typeof HardDrive }> = [
  { id: 'local', title: 'En mi dispositivo', description: 'En tu carpeta, disco USB o NAS montado.', note: 'Te pediremos una carpeta', icon: HardDrive },
  { id: 'cloud', title: 'Sólo en la nube', description: 'En Amazon S3, disponible online mientras el servicio cloud esté activo.', note: 'No necesitas carpeta local', icon: Cloud },
  { id: 'both', title: 'Dispositivo y nube', description: 'Una copia local y una segunda réplica en Amazon S3.', note: 'Te pediremos una carpeta', icon: Cloud },
]

function sourceNames(selected: BackupSourceId[]) {
  return sources.filter((source) => selected.includes(source.id)).map((source) => source.title).join(' · ')
}

function pluralPeriod(amount: number, unit: PeriodUnit) {
  return `${amount} ${unit === 'years' ? amount === 1 ? 'año' : 'años' : amount === 1 ? 'mes' : 'meses'}`
}

function dateFromToday(amount: number, unit: PeriodUnit) {
  const date = new Date()
  if (unit === 'years') date.setFullYear(date.getFullYear() - amount)
  else date.setMonth(date.getMonth() - amount)
  return date.toISOString().slice(0, 10)
}

function makeEffectiveDate(scope: SelectionScope | null, timeMode: SelectionTimeMode, amount: number, unit: PeriodUnit, after: string, before: string): EffectiveDate {
  if (!scope) return { datePreset: 'all', label: 'Alcance pendiente' }
  if (scope === 'all') return { datePreset: 'all', label: 'Todo el contenido' }
  if (timeMode === 'recent') {
    return { datePreset: 'custom', after: dateFromToday(amount, unit), label: `Últimos ${pluralPeriod(amount, unit)}` }
  }
  return {
    datePreset: 'custom',
    after: after || undefined,
    before: before || undefined,
    label: after || before ? `${after || 'Sin inicio'} — ${before || 'Sin fin'}` : 'Intervalo pendiente',
  }
}

function gmailQueryForDate(date: EffectiveDate) {
  if (date.datePreset !== 'custom') return ''
  const terms: string[] = []
  if (date.after) terms.push(`after:${date.after.replaceAll('-', '/')}`)
  if (date.before) terms.push(`before:${date.before.replaceAll('-', '/')}`)
  return terms.join(' ')
}

function configuredRules(organization: OrganizationMode, date: EffectiveDate): CopyRules {
  const rules = rulesForRecipe('all-drive')
  rules.organization = organization
  rules.age = date.datePreset
  rules.after = date.after
  rules.before = date.before
  return rules
}

function StepProgress({ step }: { step: number }) {
  const labels = ['Tu Google', 'Copiar o limpiar', 'Analiza y guarda']
  return <nav className="wizard-step-progress" aria-label={`Paso ${step} de 3`}>
    {labels.map((label, index) => {
      const number = index + 1
      const complete = number < step
      const current = number === step
      return <Fragment key={label}>
        <div className={`${current ? 'is-current' : ''} ${complete ? 'is-complete' : ''}`} aria-current={current ? 'step' : undefined}>
          <span>{complete ? <Check size={18} /> : number}</span>
          <strong>{label}</strong>
        </div>
        {number < labels.length && <i aria-hidden="true" />}
      </Fragment>
    })}
  </nav>
}

export default function BackupWizard() {
  const [step, setStep] = useState(1)
  const stepHeadingRef = useRef<HTMLHeadingElement>(null)
  const [selectedSources, setSelectedSources] = useState<BackupSourceId[]>([])
  const [intent, setIntent] = useState<Extract<BackupIntent, 'backup' | 'clean'> | null>(null)
  const [scope, setScope] = useState<SelectionScope | null>(null)
  const [timeMode, setTimeMode] = useState<SelectionTimeMode>('recent')
  const [periodAmount, setPeriodAmount] = useState(2)
  const [periodUnit, setPeriodUnit] = useState<PeriodUnit>('years')
  const [after, setAfter] = useState('')
  const [before, setBefore] = useState('')
  const [organization, setOrganization] = useState<OrganizationMode>('year-month-type')
  const [destinationMode, setDestinationMode] = useState<BackupDestinationMode | null>(null)
  const [destination, setDestination] = useState<FileSystemDirectoryHandle | null>(null)
  const [paymentMessage, setPaymentMessage] = useState('')
  const [paymentState, setPaymentState] = useState<'idle' | 'opening' | 'paid' | 'error'>('idle')
  const [analysis, setAnalysis] = useState<AnalysisState>({ status: 'idle', items: [], message: '' })
  const [gmailAnalysis, setGmailAnalysis] = useState<GmailPreflightSummary>({ status: 'idle', count: 0, estimatedBytes: 0, sampledCount: 0, message: '' })
  const [analysisKey, setAnalysisKey] = useState(0)
  const [cloudQuote, setCloudQuote] = useState<CloudQuote | null>(null)

  const localSupported = supportsLocalDestination()
  // El Client ID OAuth web es público; el Client Secret nunca se incluye aquí.
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '682296387506-ld4tn30ativ6n0t9e7mt8c149duvqrvs.apps.googleusercontent.com'
  const clientReady = isValidClientId(clientId)
  const driveSelected = selectedSources.includes('drive')
  const gmailSelected = selectedSources.includes('gmail')
  const photosSelected = selectedSources.includes('photos')
  const cloudSelected = destinationMode === 'cloud' || destinationMode === 'both'
  const requiresLocalFolder = destinationMode === 'local' || destinationMode === 'both'
  const dateSelection = useMemo(() => makeEffectiveDate(scope, timeMode, periodAmount, periodUnit, after, before), [scope, timeMode, periodAmount, periodUnit, after, before])
  const analysisRules = useMemo(() => configuredRules(organization, dateSelection), [organization, dateSelection])
  const matchingItems = useMemo(() => analysis.items.filter((item) => fileMatchesRules(item, analysisRules)), [analysis.items, analysisRules])
  const knownDriveBytes = useMemo(() => matchingItems.reduce((total, item) => total + Number(item.size ?? 0), 0), [matchingItems])
  const unknownDriveSize = useMemo(() => matchingItems.filter((item) => !item.size).length, [matchingItems])
  const totalDirectItems = matchingItems.length + gmailAnalysis.count
  const directAnalyzedBytes = knownDriveBytes + gmailAnalysis.estimatedBytes
  const driveAnalysisReady = !driveSelected || analysis.status === 'ready'
  const gmailAnalysisReady = !gmailSelected || gmailAnalysis.status === 'ready'
  const hasDirectAnalysisSource = driveSelected || gmailSelected
  const directAnalysisComplete = hasDirectAnalysisSource && driveAnalysisReady && gmailAnalysisReady
  const hasDirectResults = totalDirectItems > 0
  const analysisReadyForDestination = directAnalysisComplete && hasDirectResults && !photosSelected
  const destinationReady = analysisReadyForDestination && Boolean(destinationMode) && (!requiresLocalFolder || Boolean(destination))
  const cloudReadyForCheckout = !cloudSelected || Boolean(cloudQuote?.uploadsEnabled)
  const checkoutReady = destinationReady && cloudReadyForCheckout
  const gmailQuery = useMemo(() => gmailQueryForDate(dateSelection), [dateSelection])

  const analysisPhase = !hasDirectAnalysisSource
    ? 'photos'
    : driveSelected && !driveAnalysisReady
      ? 'drive'
      : gmailSelected && !gmailAnalysisReady
        ? 'gmail'
        : photosSelected
          ? 'photos'
          : !hasDirectResults
            ? 'empty'
            : 'destination'

  useEffect(() => {
    const query = new URLSearchParams(window.location.search)
    const sessionId = query.get('session_id')
    if (query.get('checkout') === 'cancelled') setPaymentMessage('El pago se canceló. No se ha borrado nada y tu selección sigue en este navegador.')
    if (!sessionId) return
    setPaymentState('opening')
    fetch(`/api/checkout/session/${encodeURIComponent(sessionId)}`)
      .then(async (response) => ({ response, payload: await response.json() as { paid?: boolean; status?: string; error?: string } }))
      .then(({ response, payload }) => {
        if (!response.ok || !payload.paid) throw new Error(payload.status === 'expired' ? `Tu licencia de ${licenseDurationLabel} ha caducado. Puedes analizar de nuevo y comprar otra licencia, sin cobro automático.` : payload.error || 'Stripe aún no ha confirmado el pago.')
        localStorage.setItem('libregoo.checkout-session', sessionId)
        setPaymentState('paid')
        setPaymentMessage(`Pago confirmado. Tu licencia para una cuenta Google está activa durante ${licenseDurationLabel} en este navegador.`)
      })
      .catch((error: unknown) => {
        setPaymentState('error')
        setPaymentMessage(error instanceof Error ? error.message : 'No se pudo verificar el pago todavía.')
      })
  }, [])

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const heading = stepHeadingRef.current
      if (!heading) return
      const top = heading.getBoundingClientRect().top + window.scrollY - 112
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
      heading.focus({ preventScroll: true })
    })
    return () => window.cancelAnimationFrame(frame)
  }, [step])

  const invalidateAnalysis = () => {
    setAnalysis((current) => current.status === 'idle' ? current : { status: 'idle', items: [], message: 'La selección ha cambiado. Analiza otra vez antes de guardar.' })
    setGmailAnalysis({ status: 'idle', count: 0, estimatedBytes: 0, sampledCount: 0, message: '' })
    setCloudQuote(null)
    setAnalysisKey((current) => current + 1)
  }

  const createPlan = (): PendingBackupPlan | null => {
    const fail = (message: string) => {
      setPaymentState('error')
      setPaymentMessage(message)
      return null
    }
    if (!selectedSources.length) return fail('Elige al menos una fuente de Google.')
    if (!intent) return fail('Elige si quieres copiar o limpiar.')
    if (!analysisReadyForDestination) return fail('Termina el análisis real antes de continuar al destino.')
    if (!destinationMode) return fail('Elige dónde quieres guardar la copia.')
    if (requiresLocalFolder && !destination) return fail('Elige una carpeta para la copia local.')
    if (dateSelection.datePreset === 'custom' && dateSelection.after && dateSelection.before && dateSelection.after > dateSelection.before) return fail('Revisa el intervalo: la fecha inicial no puede ser posterior a la final.')

    return {
      version: 1,
      sources: selectedSources,
      intent,
      profile: 'complete',
      organization,
      datePreset: dateSelection.datePreset,
      after: dateSelection.after,
      before: dateSelection.before,
      gmailQuery,
      destinationMode,
      cloudSelected,
      savedAt: new Date().toISOString(),
    }
  }

  const persistPlan = (plan: PendingBackupPlan) => {
    localStorage.setItem('libregoo.pending-plan', JSON.stringify(plan))
  }

  const toggleSource = (source: BackupSourceId) => {
    setSelectedSources((current) => current.includes(source) ? current.filter((item) => item !== source) : [...current, source])
    setDestinationMode(null)
    setDestination(null)
    invalidateAnalysis()
  }

  const chooseIntent = (next: Extract<BackupIntent, 'backup' | 'clean'>) => {
    setIntent(next)
    invalidateAnalysis()
  }

  const chooseScope = (next: SelectionScope) => {
    setScope(next)
    invalidateAnalysis()
  }

  const chooseDestinationMode = (next: BackupDestinationMode) => {
    if (gmailSelected && next === 'cloud') return
    setDestinationMode(next)
    if (next === 'cloud') setDestination(null)
    setPaymentMessage('')
    setCloudQuote(null)
  }

  const chooseDestination = async () => {
    try {
      const handle = await pickDestination()
      setDestination(handle)
      setPaymentMessage('')
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return
      setPaymentState('error')
      setPaymentMessage(error instanceof Error ? error.message : 'No se pudo seleccionar la carpeta local.')
    }
  }

  const connectAndAnalyzeDrive = async () => {
    if (!driveSelected || !clientReady) return
    try {
      setAnalysis({ status: 'connecting', items: [], message: 'Abriendo el consentimiento oficial de Google…' })
      const token = await authorizeDrive(clientId)
      setAnalysis({ status: 'analyzing', items: [], message: 'Analizando Drive: sólo leemos metadatos para calcular archivos y tamaño.' })
      const items = await listDriveItems(token, false, (count) => setAnalysis({ status: 'analyzing', items: [], message: `Analizando Drive: ${count.toLocaleString('es-ES')} elementos encontrados…` }))
      setAnalysis({ status: 'ready', items, message: 'Drive analizado. Revisa el alcance completo antes de decidir el destino.' })
    } catch (error) {
      setAnalysis({ status: 'error', items: [], message: error instanceof Error ? error.message : 'No se pudo analizar Drive.' })
    }
  }

  const openCheckout = async () => {
    const plan = createPlan()
    if (!plan || !checkoutReady) return
    persistPlan(plan)
    setPaymentState('opening')
    setPaymentMessage('Preparando el pago seguro en Stripe…')
    try {
      const response = await fetch('/api/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...plan,
          destinationName: requiresLocalFolder ? destination?.name : undefined,
          analysis: {
            source: sourceNames(selectedSources),
            fileCount: totalDirectItems,
            knownBytes: directAnalyzedBytes,
            driveFileCount: matchingItems.length,
            driveKnownBytes: knownDriveBytes,
            driveUnknownSize: unknownDriveSize,
            gmailMessageCount: gmailAnalysis.count,
            gmailEstimatedBytes: gmailAnalysis.estimatedBytes,
            gmailSampledCount: gmailAnalysis.sampledCount,
          },
        }),
      })
      const payload = await response.json() as { url?: string; error?: string }
      if (!response.ok || !payload.url) throw new Error(payload.error || 'No se pudo iniciar el pago.')
      const opened = window.open(payload.url, '_blank', 'noopener,noreferrer')
      if (!opened) window.location.assign(payload.url)
      setPaymentState('idle')
      setPaymentMessage('Stripe se abrió en una pestaña nueva. Cuando termines, vuelve aquí para continuar con tu copia.')
    } catch (error) {
      setPaymentState('error')
      setPaymentMessage(error instanceof Error ? error.message : 'No se pudo iniciar el pago.')
    }
  }

  const canAdvance = step === 1
    ? selectedSources.length > 0
    : step === 2
      ? Boolean(intent) && Boolean(scope) && (scope === 'all' || timeMode === 'recent' || Boolean(after || before)) && !(timeMode === 'range' && after && before && after > before)
      : true
  const preparingDrive = analysis.status === 'connecting' || analysis.status === 'analyzing'
  const currentTask = analysisPhase === 'photos'
    ? { title: 'Google Fotos necesita su importación local guiada.', detail: 'La API oficial no permite inventariar y descargar automáticamente la biblioteca existente. Cambia la selección si quieres continuar hoy con Gmail o Drive.' }
    : analysisPhase === 'drive'
      ? { title: 'Conecta y analiza Google Drive.', detail: 'Sólo leeremos los metadatos que calculan archivos y tamaño. No borraremos, moveremos ni modificaremos nada.' }
      : analysisPhase === 'gmail'
        ? { title: 'Conecta y analiza Gmail.', detail: 'Contaremos los correos y estimaremos cuánto ocupan antes de pedirte un destino o pago.' }
        : analysisPhase === 'empty'
          ? { title: 'No hay contenido que coincida con esta selección.', detail: 'Vuelve al paso anterior y amplía el periodo o cambia las fuentes antes de continuar.' }
          : { title: 'El análisis está listo: decide el destino.', detail: 'Ahora puedes elegir dispositivo, Amazon S3 o ambos. Si eliges nube, calcularemos el coste antes de cobrar o transferir.' }

  return <main className="wizard-shell">
    <header className="wizard-topbar">
      <a className="brand wordmark-logo" href="/" aria-label="Volver al inicio de libregoo">libregoo</a>
      <a className="wizard-exit" href="/"><ArrowLeft size={15} /> Salir del asistente</a>
    </header>

    <section className="wizard-card">
      {step === 1 && <div className="wizard-step">
        <span className="kicker">CONEXIÓN DIRECTA CON GOOGLE</span>
        <h1 ref={stepHeadingRef} tabIndex={-1}>Elige qué quieres incluir.</h1>
        <StepProgress step={step} />
        <p className="wizard-lead">Marca una o varias fuentes. En el siguiente paso decidirás si quieres copiar o limpiar.</p>
        <div className="wizard-source-grid">
          {sources.map((source) => {
            const Icon = source.icon
            const active = selectedSources.includes(source.id)
            return <button type="button" className={`wizard-source ${active ? 'is-selected' : ''}`} key={source.id} onClick={() => toggleSource(source.id)} aria-pressed={active}>
              <span className="wizard-source-icon"><Icon size={22} /></span>
              <span><strong>{source.title}</strong><small>{source.description}</small><em>{source.detail}</em></span>
              <b>{active ? <Check size={16} /> : '+'}</b>
            </button>
          })}
        </div>
        {!canAdvance && <p className="wizard-choice-hint" aria-live="polite">Marca al menos una fuente para continuar.</p>}
      </div>}

      {step === 2 && <div className="wizard-step">
        <span className="kicker">DEFINE TU ACCIÓN</span>
        <h1 ref={stepHeadingRef} tabIndex={-1}>¿Qué quieres hacer?</h1>
        <StepProgress step={step} />
        <p className="wizard-lead">Decide si quieres crear una copia o preparar una limpieza segura. Después acota el contenido sólo si lo necesitas.</p>
        <section className="wizard-purpose" aria-labelledby="purpose-title">
          <div className="wizard-section-heading"><p className="kicker">TU ACCIÓN</p><h2 id="purpose-title">Copiar o limpiar</h2></div>
          <div className="wizard-purpose-grid">
            {intents.map((item) => <button type="button" className={intent === item.id ? 'is-selected' : ''} key={item.id} onClick={() => chooseIntent(item.id)} aria-pressed={intent === item.id}>
              <i>{intent === item.id && <Check size={16} />}</i>
              <strong>{item.title}</strong>
              <small>{item.description}</small>
            </button>)}
          </div>
        </section>

        {intent && <section className="wizard-scope" aria-labelledby="scope-title">
          <div className="wizard-section-heading"><p className="kicker">ALCANCE</p><h2 id="scope-title">¿Todo o una selección?</h2></div>
          <div className="wizard-scope-grid">
            <button type="button" className={scope === 'all' ? 'is-selected' : ''} onClick={() => chooseScope('all')} aria-pressed={scope === 'all'}><i>{scope === 'all' && <Check size={16} />}</i><strong>Todo</strong><small>{intent === 'backup' ? 'Incluye todo el contenido de las fuentes elegidas.' : 'Analiza todo el contenido elegido para preparar una limpieza.'}</small></button>
            <button type="button" className={scope === 'selection' ? 'is-selected' : ''} onClick={() => chooseScope('selection')} aria-pressed={scope === 'selection'}><i>{scope === 'selection' && <Check size={16} />}</i><strong>Una selección</strong><small>Acota por los últimos N meses/años o por un intervalo concreto.</small></button>
          </div>

          {scope === 'selection' && <div className="selection-filter-panel">
            <div className="selection-filter-tabs" role="group" aria-label="Tipo de selección por fechas">
              <button type="button" className={timeMode === 'recent' ? 'is-selected' : ''} onClick={() => { setTimeMode('recent'); invalidateAnalysis() }}>Últimos N</button>
              <button type="button" className={timeMode === 'range' ? 'is-selected' : ''} onClick={() => { setTimeMode('range'); invalidateAnalysis() }}>Intervalo de fechas</button>
            </div>
            {timeMode === 'recent' ? <div className="selection-relative"><span>Incluye contenido de los últimos</span><input type="number" min="1" max="99" value={periodAmount} onChange={(event) => { setPeriodAmount(Math.min(99, Math.max(1, Number(event.target.value) || 1))); invalidateAnalysis() }} aria-label="Cantidad de meses o años" /><select value={periodUnit} onChange={(event) => { setPeriodUnit(event.target.value as PeriodUnit); invalidateAnalysis() }} aria-label="Unidad de periodo"><option value="years">años</option><option value="months">meses</option></select><small>Desde {dateFromToday(periodAmount, periodUnit)} hasta hoy.</small></div> : <div className="wizard-date-range"><label>Desde<input type="date" value={after} onChange={(event) => { setAfter(event.target.value); invalidateAnalysis() }} /></label><label>Hasta<input type="date" value={before} onChange={(event) => { setBefore(event.target.value); invalidateAnalysis() }} /></label></div>}
          </div>}
        </section>}

        {intent && <label className="wizard-organization"><span>CÓMO ORGANIZAR LA COPIA</span><select value={organization} onChange={(event) => { setOrganization(event.target.value as OrganizationMode); invalidateAnalysis() }}><option value="year-month-type">Año / mes / tipo</option><option value="year-type">Año / tipo</option><option value="original">Mantener estructura original</option></select><small>Esta preferencia se aplicará al crear la copia. No modifica ningún archivo en Google.</small></label>}
        {!canAdvance && <p className="wizard-choice-hint" aria-live="polite">Elige copiar o limpiar. Si has escogido una selección por fechas, completa al menos una fecha.</p>}
      </div>}

      {step === 3 && <div className="wizard-step">
        <span className="kicker">ANÁLISIS ANTES DE GUARDAR</span>
        <h1 ref={stepHeadingRef} tabIndex={-1}>{analysisPhase === 'destination' ? 'Ya conoces el tamaño de tu copia.' : 'Analiza tu selección antes de guardar.'}</h1>
        <StepProgress step={step} />
        <p className="wizard-lead">{analysisPhase === 'destination' ? 'Los resultados están listos. Elige ahora dónde quieres guardar los datos y, si seleccionas nube, verás su coste estimado.' : 'No pediremos pago hasta conocer el número de elementos y el espacio aproximado que va a ocupar tu selección.'}</p>
        <div className="analysis-current-task" aria-live="polite"><span>AHORA</span><strong>{currentTask.title}</strong><small>{currentTask.detail}</small></div>

        {analysisPhase === 'drive' && <section className={`prepayment-analysis ${analysis.status} is-current`}>
          <div className="prepayment-analysis-head"><div><span className="kicker">GOOGLE DRIVE · SOLO LECTURA</span><h2>Analiza Drive gratis.</h2><p>La conexión oficial sólo lee metadatos para calcular los archivos incluidos y su tamaño. No borra, mueve ni modifica nada.</p></div><button type="button" className="button button-primary analysis-button" onClick={connectAndAnalyzeDrive} disabled={preparingDrive || !clientReady}><RefreshCcw className={preparingDrive ? 'spin' : ''} size={18} /> {!clientReady ? 'Conexión Google pendiente' : preparingDrive ? 'Analizando…' : 'Conectar y analizar Drive'}</button></div>
          {!clientReady && <div className="analysis-activation"><ShieldCheck size={19} /><div><strong>Conexión directa preparada</strong><p>Necesitas completar la configuración OAuth de Google para analizar Drive con esta cuenta.</p></div></div>}
          {analysis.message && <p className={analysis.status === 'error' ? 'analysis-message is-error' : 'analysis-message'}>{analysis.message}</p>}
          <div className="analysis-expectations"><div><span>01</span><strong>Archivos incluidos</strong><small>Verás cuántos elementos coinciden con {dateSelection.label.toLowerCase()}.</small></div><div><span>02</span><strong>Tamaño real</strong><small>Calcularemos el volumen conocido antes de guardar.</small></div><div><span>03</span><strong>Sin cambios</strong><small>El permiso solicitado es de sólo lectura.</small></div></div>
        </section>}

        {analysisPhase === 'gmail' && <GmailPreflight clientId={clientId} clientReady={clientReady} resetKey={analysisKey} query={gmailQuery} selectionLabel={dateSelection.label} onChange={setGmailAnalysis} />}

        {analysisPhase === 'photos' && <section className="prepayment-analysis photos-analysis is-current"><div className="prepayment-analysis-head"><div><span className="kicker">GOOGLE FOTOS · IMPORTACIÓN OFICIAL</span><h2>La biblioteca completa requiere una importación local.</h2><p>Google no permite a una aplicación listar y descargar automáticamente toda tu biblioteca existente. Para seleccionar elementos puntuales usaremos el Picker oficial; para toda la biblioteca, el importador local del archivo oficial.</p></div><button type="button" className="button button-secondary analysis-button" onClick={() => setStep(1)}>Cambiar fuentes <ArrowRight size={17} /></button></div><div className="analysis-activation"><Images size={19} /><div><strong>Sin estimación falsa de Google Fotos</strong><p>Esta fuente no se incluye en el tamaño ni en el pago hasta que su importación oficial esté disponible. Puedes quitar Fotos y continuar ahora con Gmail o Drive.</p></div></div></section>}

        {analysisPhase === 'empty' && <section className="prepayment-analysis is-current"><div className="prepayment-analysis-head"><div><span className="kicker">SIN RESULTADOS</span><h2>No hemos encontrado contenido en este alcance.</h2><p>La conexión y el análisis han terminado correctamente. Vuelve al paso anterior para ampliar la selección y vuelve a analizar.</p></div><button type="button" className="button button-secondary analysis-button" onClick={() => setStep(2)}>Cambiar selección <ArrowRight size={17} /></button></div></section>}

        {directAnalysisComplete && hasDirectResults && <section className="prepayment-analysis analysis-complete-card">
          <div className="prepayment-analysis-head"><div><span className="kicker">ANÁLISIS COMPLETADO</span><h2>Esto es lo que vas a {intent === 'clean' ? 'revisar para limpiar' : 'guardar'}.</h2><p>Estas cifras están a contraste completo porque son el resultado de tu análisis. Puedes volver atrás para modificar fuentes o fechas.</p></div><ShieldCheck size={28} /></div>
          <div className="analysis-result">
            {driveSelected && <><div><strong>{matchingItems.length.toLocaleString('es-ES')}</strong><span>archivos de Drive incluidos</span></div><div><strong>{formatBytes(knownDriveBytes)}</strong><span>tamaño conocido de Drive</span></div><div><strong>{unknownDriveSize.toLocaleString('es-ES')}</strong><span>archivos de Drive con tamaño por confirmar</span></div></>}
            {gmailSelected && <><div><strong>{gmailAnalysis.count.toLocaleString('es-ES')}</strong><span>correos de Gmail incluidos</span></div><div><strong>≈ {formatBytes(gmailAnalysis.estimatedBytes)}</strong><span>tamaño estimado de Gmail{gmailAnalysis.sampledCount ? ` · muestra de ${gmailAnalysis.sampledCount}` : ''}</span></div></>}
          </div>
          <div className="analysis-result-note"><strong>{totalDirectItems.toLocaleString('es-ES')} elementos directos · {formatBytes(directAnalyzedBytes)} en total conocido o estimado.</strong><span>{intent === 'clean' ? `Si verificas la copia y después eliminas este contenido directamente en Google, podrías liberar aproximadamente ${formatBytes(directAnalyzedBytes)}. libregoo no borra nada automáticamente.` : 'Esta es la estimación que usaremos para decidir el destino y cotizar nube si la eliges.'}</span></div>
          {photosSelected && <p className="analysis-message source-needed">Google Fotos queda pendiente de su importación oficial y no forma parte de estas cifras. Quita esa fuente para continuar con Gmail o Drive ahora.</p>}
        </section>}

        {analysisReadyForDestination && <section className="destination-decision" aria-labelledby="destination-question">
          <div className="destination-decision-heading"><span className="destination-decision-icon"><Cloud size={24} /></span><div><p className="kicker">DESTINO DE LA COPIA</p><h2 id="destination-question">¿Dónde quieres guardarla?</h2><p>Ahora que conoces el tamaño, escoge dispositivo, Amazon S3 o ambos. Sólo te pediremos carpeta para las opciones locales.</p></div></div>
          <div className="destination-choice-grid">
            {destinationOptions.map((option) => {
              const Icon = option.icon
              const selected = destinationMode === option.id
              const unavailable = gmailSelected && option.id === 'cloud'
              return <label className={`destination-choice ${option.id === 'both' ? 'destination-choice-featured' : ''} ${selected ? 'is-selected' : ''} ${unavailable ? 'is-disabled' : ''}`} key={option.id}>
                <input type="radio" name="destination" checked={selected} disabled={unavailable} onChange={() => chooseDestinationMode(option.id)} />
                <Icon size={20} />
                <span><strong>{option.title}</strong><small>{unavailable ? 'Gmail se guarda actualmente en una carpeta local.' : option.description}</small><em>{unavailable ? 'Elige dispositivo o ambos' : option.note}</em></span>
              </label>
            })}
          </div>
          {cloudSelected && <div className="cloud-access-note"><Cloud size={18} /><div><strong>La copia cloud se guarda en Amazon S3.</strong><p>Verás el coste estimado antes de iniciar un pago cloud o transferir archivos. Su disponibilidad depende de que el servicio cloud esté activo.</p></div></div>}
        </section>}

        {analysisReadyForDestination && requiresLocalFolder && <div className={`wizard-destination ${destination ? 'is-selected' : ''}`}>
          <div><span>DESTINO LOCAL</span><strong>{destination ? `Ubicación seleccionada: ${destination.name}` : 'Elige una carpeta para tu copia'}</strong><small>{destination ? 'El navegador confirma esta carpeta. Por seguridad no expone la ruta completa del sistema.' : localSupported ? 'Puede ser tu ordenador, USB o un NAS montado. El navegador pedirá permiso antes de escribir.' : 'Necesitas Chrome o Edge para autorizar una carpeta local.'}</small>{destination && <em><CheckCircle2 size={15} /> Carpeta autorizada para esta sesión</em>}</div>
          <button type="button" className="button button-secondary" onClick={chooseDestination} disabled={!localSupported}><FolderOpen size={17} /> {destination ? 'Cambiar carpeta' : 'Elegir carpeta'}</button>
        </div>}

        {analysisReadyForDestination && cloudSelected && matchingItems.length > 0 && <><CloudVaultCard totalBytes={knownDriveBytes} objectCount={matchingItems.length} selected showToggle={false} destinationMode={destinationMode ?? 'both'} onSelected={() => undefined} onQuote={setCloudQuote} />{gmailSelected && <p className="wizard-cloud-note"><Mail size={16} /> Esta cotización cloud cubre Drive. El backup directo de Gmail se guarda localmente por ahora y su tamaño estimado no se incluye en Amazon S3.</p>}</>}

        {analysisReadyForDestination && <div className={`wizard-checkout ${checkoutReady ? 'is-current' : 'is-locked'}`} aria-disabled={!checkoutReady}><div><span className="offer-pill"><Sparkles size={14} /> OFERTA DE LANZAMIENTO</span><p><s>9,90 €</s> <strong>4,90 €</strong> <span>licencia de {licenseDurationLabel} · 1 cuenta Google</span></p><small>{!destinationMode ? 'Elige primero el destino de tu copia.' : requiresLocalFolder && !destination ? 'Elige una carpeta local para activar el pago.' : cloudSelected && !cloudReadyForCheckout ? 'Amazon S3 aún no está activado. Puedes usar local o volver cuando el servicio cloud esté disponible.' : `La licencia será válida durante ${licenseDurationLabel} desde el pago. Stripe mostrará el importe y los impuestos aplicables antes del cobro.`}</small></div>{paymentState === 'paid' ? <a className="button button-primary" href={gmailSelected ? '/?workspace=1#gmail' : '/?workspace=1#drive'} onClick={() => { const plan = createPlan(); if (plan) persistPlan(plan) }}>Abrir mi espacio de copia <ArrowRight size={17} /></a> : <button type="button" className="button button-primary" onClick={openCheckout} disabled={paymentState === 'opening' || !checkoutReady}><CreditCard size={17} /> {paymentState === 'opening' ? 'Preparando…' : 'Continuar al pago seguro'}</button>}</div>}
        {paymentMessage && <p className={`wizard-payment-message ${paymentState === 'error' ? 'is-error' : ''}`} aria-live="polite">{paymentMessage}</p>}
        {analysisReadyForDestination && <p className="wizard-safe"><ShieldCheck size={16} /> En local, los archivos van directamente de Google a la carpeta elegida. Stripe sólo procesa el pago.</p>}
      </div>}

      <footer className="wizard-actions"><button type="button" className="wizard-back" onClick={() => setStep((current) => Math.max(1, current - 1))} disabled={step === 1}><ArrowLeft size={16} /> Atrás</button>{step < 3 && <button type="button" className="button button-primary" onClick={() => setStep((current) => current + 1)} disabled={!canAdvance}>Continuar <ArrowRight size={17} /></button>}</footer>
      <p className="wizard-legal-links">Antes de continuar puedes consultar la <a href="/privacidad.html" target="_blank" rel="noreferrer">Política de privacidad</a>, las <a href="/condiciones.html" target="_blank" rel="noreferrer">Condiciones de copia</a> y el <a href="/legal.html" target="_blank" rel="noreferrer">Aviso legal</a>.</p>
    </section>
  </main>
}
